import { useEffect, useState } from 'react';
import type { Editor, EditorEvents } from '@tiptap/core';
import { paginateDocument, type SheetLayout, type SheetMode } from './sheetPagination';

/** Zo lang moet de layout ongewijzigd blijven voordat de PDF-export hem vastlegt. */
const LAYOUT_QUIET_MS = 300;

/** Elk vel krijgt de achtergrond van zijn blok, ook onder de inhoud en op vervolgvellen. */
function syncPhysicalPageBackgrounds(root: HTMLElement) {
  root.querySelectorAll<HTMLElement>(':scope > .react-renderer').forEach((page) => {
    const contentRoot = page.firstElementChild;
    if (!(contentRoot instanceof HTMLElement)) return;

    const source = getComputedStyle(contentRoot);
    const sourceColor = contentRoot.style.backgroundColor || source.backgroundColor;
    const color = sourceColor === 'rgba(0, 0, 0, 0)' ? '#ffffff' : sourceColor;
    const image = contentRoot.style.backgroundImage || source.backgroundImage;
    const updates: Record<string, string> = {
      backgroundColor: color,
      backgroundImage: image,
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
    };

    Object.entries(updates).forEach(([property, value]) => {
      const style = page.style as unknown as Record<string, string>;
      if (style[property] !== value) style[property] = value;
    });
  });
}

function isOverlayMutation(record: MutationRecord): boolean {
  const inOverlay = (node: Node | null) =>
    (node instanceof Element ? node : node?.parentElement)?.closest('[data-sheet-overlay]') != null;
  if (inOverlay(record.target)) return true;
  if (record.type !== 'childList') return false;
  const nodes = [...Array.from(record.addedNodes), ...Array.from(record.removedNodes)];
  return (
    nodes.length > 0 &&
    nodes.every((node) => node instanceof Element && node.hasAttribute('data-sheet-overlay'))
  );
}

/**
 * Verdeelt het document opnieuw over A4-vellen (`paginateDocument`) zodra de
 * inhoud verandert: in de editor, de klantweergave en de PDF dezelfde vellen.
 * `ready` wordt waar als de layout even stilstaat met geladen lettertypen en
 * afbeeldingen; daarop wacht de PDF-export.
 */
export function useSheetLayout(editor: Editor | null, mode: SheetMode, renderHash?: string) {
  const [layout, setLayout] = useState<SheetLayout | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!editor) return;
    const root = editor.view.dom as HTMLElement;
    let disposed = false;
    let frame = 0;
    let quietTimer = 0;
    let previous = '';

    const run = () => {
      frame = 0;
      // Niet midden in een samengesteld teken (IME); compositionend plant opnieuw in.
      if (disposed || editor.isDestroyed || editor.view.composing) return;
      syncPhysicalPageBackgrounds(root);
      const next = paginateDocument(editor.view, mode);
      // Eigen DOM-wijzigingen van deze ronde mogen geen nieuwe ronde starten.
      observer.takeRecords();
      const key = JSON.stringify(next);
      if (key !== previous) {
        previous = key;
        setLayout(next);
      }
      window.clearTimeout(quietTimer);
      quietTimer = window.setTimeout(() => {
        if (disposed) return;
        const imagesLoaded = Array.from(root.querySelectorAll('img')).every(
          (image) => image.complete
        );
        setReady(document.fonts.status === 'loaded' && imagesLoaded && next.sheetCount > 0);
      }, LAYOUT_QUIET_MS);
    };
    const schedule = () => {
      window.clearTimeout(quietTimer);
      setReady(false);
      if (!frame) frame = requestAnimationFrame(run);
    };

    const observer = new MutationObserver((records) => {
      if (records.some((record) => !isOverlayMutation(record))) schedule();
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    // Een gewijzigd attribuut (breedte van een afbeelding, kolommen) muteert soms alleen stijlen.
    const onTransaction = ({ transaction }: EditorEvents['transaction']) => {
      if (transaction.docChanged) schedule();
    };
    editor.on('transaction', onTransaction);
    // Afbeeldingen en lettertypen veranderen de hoogte zonder DOM-mutatie.
    root.addEventListener('load', schedule, true);
    root.addEventListener('compositionend', schedule);
    document.fonts.addEventListener('loadingdone', schedule);
    void document.fonts.ready.then(schedule);
    schedule();

    return () => {
      disposed = true;
      observer.disconnect();
      editor.off('transaction', onTransaction);
      root.removeEventListener('load', schedule, true);
      root.removeEventListener('compositionend', schedule);
      document.fonts.removeEventListener('loadingdone', schedule);
      cancelAnimationFrame(frame);
      window.clearTimeout(quietTimer);
    };
  }, [editor, mode, renderHash]);

  return { layout, ready };
}
